import type {
  BetaAccessRequest,
  BetaAllowlistEntry,
  BetaAccessType,
  BetaAccessRequestStatus,
  BetaAllowlistStatus,
} from "@crewledger/shared/beta-access";
import { apiFetch } from "@/lib/api";

type ListMeta = { total?: number; page?: number; pageSize?: number };

export async function fetchBetaAccessAdminAccess() {
  return apiFetch<{ data: { allowed: boolean } }>("/api/admin/beta-access/access");
}

export async function fetchBetaAccessRequests(params?: {
  page?: number;
  pageSize?: number;
  status?: BetaAccessRequestStatus;
  q?: string;
}) {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.pageSize) search.set("pageSize", String(params.pageSize));
  if (params?.status) search.set("status", params.status);
  if (params?.q) search.set("q", params.q);

  const qs = search.toString();
  return apiFetch<{ data: BetaAccessRequest[]; meta: ListMeta }>(
    `/api/admin/beta-access/requests${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchBetaAllowlist(params?: {
  page?: number;
  pageSize?: number;
  status?: BetaAllowlistStatus;
  accessType?: BetaAccessType;
  q?: string;
}) {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.pageSize) search.set("pageSize", String(params.pageSize));
  if (params?.status) search.set("status", params.status);
  if (params?.accessType) search.set("accessType", params.accessType);
  if (params?.q) search.set("q", params.q);

  const qs = search.toString();
  return apiFetch<{ data: BetaAllowlistEntry[]; meta: ListMeta }>(
    `/api/admin/beta-access/allowlist${qs ? `?${qs}` : ""}`,
  );
}

export async function approveBetaAccessRequest(
  requestId: string,
  body?: { accessType?: BetaAccessType; companyName?: string },
) {
  return apiFetch<{
    data: { request: BetaAccessRequest; allowlist: BetaAllowlistEntry };
  }>(`/api/admin/beta-access/requests/${requestId}/approve`, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export async function rejectBetaAccessRequest(requestId: string, reason?: string) {
  return apiFetch<{ data: { request: BetaAccessRequest } }>(
    `/api/admin/beta-access/requests/${requestId}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function addBetaAllowlistEntry(body: {
  email: string;
  accessType?: BetaAccessType;
  companyName?: string;
  status?: BetaAllowlistStatus;
}) {
  return apiFetch<{ data: { entry: BetaAllowlistEntry } }>("/api/admin/beta-access/allowlist", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function blockBetaAllowlistEmail(email: string) {
  return apiFetch<{ data: { entry: BetaAllowlistEntry } }>(
    `/api/admin/beta-access/allowlist/${encodeURIComponent(email)}/block`,
    { method: "POST" },
  );
}

export async function updateBetaAllowlistEntry(
  email: string,
  body: {
    accessType?: BetaAccessType;
    status?: BetaAllowlistStatus;
    companyName?: string | null;
  },
) {
  return apiFetch<{ data: { entry: BetaAllowlistEntry } }>(
    `/api/admin/beta-access/allowlist/${encodeURIComponent(email)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}
